import React, { useState, useEffect, useContext, useCallback, useRef } from 'react';
import axios from 'axios';
import { useParams, useNavigate, Navigate } from 'react-router-dom';
import { AuthContext } from '../contexts/AuthContext';
import { useCart } from '../contexts/CartContext';
import DescriptionCell from '../components/DescriptionCell';
import './Categories.css';
import './MenuLanding.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';
const PUB = `${API_URL}/menuRoutes/public`;

const resolveImg = (url) => {
  if (!url) return null;
  if (url.startsWith('http')) return url;
  return `${API_URL}${url}`;
};

const formatPrice = (price) =>
  price != null ? `₹${parseFloat(price).toFixed(2)}` : null;

const formatCutoffTime = (t) => (t ? t.slice(0, 5) : null);

const isPastCutoff = (cutoffTimeStr, orderAfterCutoff) => {
  if (!cutoffTimeStr || orderAfterCutoff === 'Y') return false;
  const [h, m] = cutoffTimeStr.split(':').map(Number);
  const cutoff = new Date();
  cutoff.setHours(h, m, 0, 0);
  return new Date() > cutoff;
};

const isAfterCutoffAllowed = (cutoffTimeStr, orderAfterCutoff) => {
  if (!cutoffTimeStr || orderAfterCutoff !== 'Y') return false;
  const [h, m] = cutoffTimeStr.split(':').map(Number);
  const cutoff = new Date();
  cutoff.setHours(h, m, 0, 0);
  return new Date() > cutoff;
};

const nextWeekend = (baseDate) => {
  const d = new Date(baseDate);
  const dow = d.getDay();
  const daysToSat = (6 - dow + 7) % 7;
  const daysToSun = (7 - dow) % 7;
  const satDate = new Date(d); satDate.setDate(d.getDate() + daysToSat);
  const sunDate = new Date(d); sunDate.setDate(d.getDate() + daysToSun);
  return satDate <= sunDate ? { sat: satDate, sun: sunDate } : { sat: sunDate, sun: satDate };
};

const fmtDate = (d) => d.toLocaleDateString('en-IN', { weekday: 'short', day: 'numeric', month: 'short' });

const computeDeliveryLabel = (product) => {
  const lead = parseInt(product.order_lead_days, 10) || 0;
  const afterCutoff = isAfterCutoffAllowed(product.order_cutoff_time, product.order_after_cutoff);
  if (product.days_available === 'WE') {
    const base = new Date();
    base.setDate(base.getDate() + lead + (afterCutoff ? 1 : 0));
    const { sat, sun } = nextWeekend(base);
    return `${fmtDate(sat)} & ${fmtDate(sun)}`;
  }
  if (!afterCutoff) return null;
  const date = new Date();
  date.setDate(date.getDate() + lead + 1);
  return date.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });
};

const comboCartId = (menuPackId, sel) => {
  const sig = Object.entries(sel)
    .filter(([, q]) => q > 0)
    .sort(([a], [b]) => Number(a) - Number(b))
    .map(([pid, q]) => `${pid}:${q}`)
    .join(',');
  return `pack_${menuPackId}_${sig}`;
};

// ── Sub-level tab row ─────────────────────────────────────────────────────────
const TabRow = ({ menus, selectedId, onSelect, loading }) => {
  if (loading) return (
    <div className="cat-tabrow cat-tabrow--loading">
      <span className="cat-tab-spinner" />
    </div>
  );
  return (
    <div className="cat-tabrow">
      <button
        className={`cat-tab ${selectedId === 'all' ? 'cat-tab--active' : ''}`}
        onClick={() => onSelect('all')}
      >
        <span className="cat-tab-icon cat-tab-icon--all">&#9733;</span>
        <span className="cat-tab-label">All</span>
      </button>
      {menus.map((menu) => (
        <button
          key={menu.id}
          className={`cat-tab ${selectedId === menu.id ? 'cat-tab--active' : ''}`}
          onClick={() => onSelect(menu.id)}
        >
          <span className="cat-tab-icon">
            {menu.image_url
              ? <img src={resolveImg(menu.image_url)} alt={menu.menu_name} />
              : <span className="cat-tab-icon--placeholder">&#128230;</span>
            }
          </span>
          <span className="cat-tab-label">{menu.menu_name}</span>
        </button>
      ))}
    </div>
  );
};

// ── Shared page-list renderer ─────────────────────────────────────────────────
const PageList = ({ pages }) => {
  if (!pages || pages.length === 0) return null;
  return (
    <div className="cat-menu-pages">
      {pages.map((page) => {
        if (page.page_type === 'Header' && page.static_banner === 'B' && page.display_text) {
          return (
            <div key={page.id} className="cat-banner-scroll">
              <span className="cat-banner-scroll-text">{page.display_text}</span>
            </div>
          );
        }
        const ctaHref = page.cta_type === 'whatsapp'
          ? `https://wa.me/${page.cta_value}${page.cta_message ? '?text=' + encodeURIComponent(page.cta_message) : ''}`
          : page.cta_type === 'url' ? page.cta_value : null;
        return (
          <div key={page.id} className={`cat-menu-page${page.page_type === 'Header' ? ' cat-menu-page--header' : ''}`}>
            {page.image_url && (
              <img src={resolveImg(page.image_url)} alt={page.display_text || ''} className="cat-menu-page-img" />
            )}
            {page.display_text && <p className="cat-menu-page-text">{page.display_text}</p>}
            {ctaHref && (
              <a
                href={ctaHref}
                target="_blank"
                rel="noopener noreferrer"
                className={`cat-menu-page-cta${page.cta_type === 'whatsapp' ? ' cat-menu-page-cta--whatsapp' : ''}`}
              >
                {page.cta_type === 'whatsapp' && <span className="cat-cta-wa-icon">&#128172;</span>}
                {page.cta_label || (page.cta_type === 'whatsapp' ? 'Chat on WhatsApp' : 'Visit Website')}
              </a>
            )}
          </div>
        );
      })}
    </div>
  );
};

// ── Image-only grid ───────────────────────────────────────────────────────────
const ImageOnlyGrid = ({ products }) => {
  const { addToCart, removeFromCart, getQuantity } = useCart();
  return (
    <div className="cat-img-only-grid">
      {products.map((product) => {
        const qty = getQuantity(product.id);
        const blocked = isPastCutoff(product.order_cutoff_time, product.order_after_cutoff);
        return (
          <div key={product.id} className="cat-img-only-card">
            <div className="cat-img-only-image-wrap">
              {product.image_url
                ? <img src={resolveImg(product.image_url)} alt={product.name} className="cat-img-only-image" />
                : <div className="cat-img-only-placeholder">&#128230;</div>
              }
            </div>
            <div className="cat-img-only-footer">
              <div className="cat-img-only-meta">
                <span className="cat-img-only-name">{product.name}</span>
                <div className="cat-img-only-price-row">
                  {formatPrice(product.price) && (
                    <span className="cat-img-only-price">{formatPrice(product.price)}</span>
                  )}
                  {formatCutoffTime(product.order_cutoff_time) && (
                    <span className="cat-img-only-cutoff">Order By {formatCutoffTime(product.order_cutoff_time)}</span>
                  )}
                </div>
              </div>
              <div className="cat-img-only-qty">
                <button className="qty-btn qty-btn--minus"
                  onClick={() => removeFromCart(product.id)}
                  disabled={qty === 0 || blocked}
                  aria-label="Remove one">&#8722;</button>
                <span className="qty-value">{qty}</span>
                <button className="qty-btn qty-btn--plus"
                  onClick={() => addToCart(product)}
                  disabled={blocked}
                  aria-label="Add one">&#43;</button>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

// ── Product grid ──────────────────────────────────────────────────────────────
const ProductGrid = ({ products, loading, error, menuPages = [] }) => {
  const { addToCart, removeFromCart, getQuantity } = useCart();

  if (loading) return (
    <div className="cat-state">
      <div className="cat-spinner" />
      <p>Loading products&hellip;</p>
    </div>
  );
  if (error) return <div className="cat-state cat-state--error"><p>{error}</p></div>;

  const headerPages = menuPages.filter((p) => p.page_type === 'Header');
  const bodyPages   = menuPages.filter((p) => p.page_type === 'Body');
  const footerPages = menuPages.filter((p) => p.page_type === 'Footer');
  const noContent   = bodyPages.length === 0 && products.length === 0;
  const useImageOnly = products.some((p) => p.show_image_only);

  const productGrid = products.length > 0 && bodyPages.length === 0 ? (
    useImageOnly ? (
      <ImageOnlyGrid products={products} />
    ) : (
      <div className="cat-product-grid">
        {products.map((product) => {
          const qty = getQuantity(product.id);
          const blocked = isPastCutoff(product.order_cutoff_time, product.order_after_cutoff);
          const deliveryLabel = qty > 0 ? computeDeliveryLabel(product) : null;
          return (
            <div key={product.id} className="cat-tile">
              <div className="cat-tile-image-wrap">
                {product.image_url
                  ? <img src={resolveImg(product.image_url)} alt={product.name} className="cat-tile-image" />
                  : <div className="cat-tile-image-placeholder">&#128230;</div>
                }
              </div>
              <div className="cat-tile-info">
                <h3 className="cat-tile-name">{product.name}</h3>
                {product.description && (
                  <DescriptionCell text={product.description} className="cat-tile-desc" />
                )}
                <div className="cat-tile-price-row">
                  {formatPrice(product.price) && (
                    <span className="cat-tile-price">{formatPrice(product.price)}</span>
                  )}
                  {formatCutoffTime(product.order_cutoff_time) && (
                    <span className="cat-tile-cutoff">Order By {formatCutoffTime(product.order_cutoff_time)}</span>
                  )}
                </div>
              </div>
              <div className="cat-tile-qty">
                <button className="qty-btn qty-btn--minus"
                  onClick={() => removeFromCart(product.id)}
                  disabled={qty === 0 || blocked}
                  aria-label="Remove one">&#8722;</button>
                <span className="qty-value">{qty}</span>
                <button className="qty-btn qty-btn--plus"
                  onClick={() => addToCart(product)}
                  disabled={blocked}
                  aria-label="Add one">&#43;</button>
                {deliveryLabel && (
                  <span className="cat-tile-delivery-label">Delivery: {deliveryLabel}</span>
                )}
              </div>
            </div>
          );
        })}
      </div>
    )
  ) : null;

  return (
    <>
      <PageList pages={headerPages} />
      {bodyPages.length > 0 && <PageList pages={bodyPages} />}
      {noContent && <div className="cat-state"><p>No products available.</p></div>}
      {productGrid}
      <PageList pages={footerPages} />
    </>
  );
};

// ── Pack customise modal (uses public endpoint) ───────────────────────────────
const PackCustomiseModal = ({ pack, onClose }) => {
  const { addToCart, cart } = useCart();

  const packCombos = Object.values(cart)
    .filter(item => item.product.isPack && item.product.menu_pack_id === pack.menu_pack_id)
    .sort((a, b) => (b.product.addedAt || 0) - (a.product.addedAt || 0));
  const lastCombo   = packCombos[0];
  const prevSel     = lastCombo?.product?.selections;
  const prevSummary = lastCombo?.product?.selectionSummary;

  const [groups, setGroups]         = useState([]);
  const [loading, setLoading]       = useState(true);
  const [error, setError]           = useState('');
  const [selections, setSelections] = useState({});

  useEffect(() => {
    axios.get(`${PUB}/pack-details`, { params: { menu_pack_id: pack.menu_pack_id } })
      .then(r => {
        const loaded = r.data;
        setGroups(loaded);
        setSelections(prev => {
          const next = { ...prev };
          for (const group of loaded) {
            if (group.products.length === 1) {
              next[group.products[0].product_id] = pack.multiples_of;
            }
          }
          return next;
        });
        setLoading(false);
      })
      .catch(() => { setError('Failed to load pack details.'); setLoading(false); });
  }, [pack.menu_pack_id]); // eslint-disable-line

  const totalSelected = Object.values(selections).reduce((s, q) => s + q, 0);
  const totalRequired = groups.length * pack.multiples_of;
  const canRepeat = !loading && groups.length > 0 && !!prevSel;

  const groupTotal = (groupId) => {
    const g = groups.find(gr => gr.group_id === groupId);
    if (!g) return 0;
    return g.products.reduce((s, p) => s + (selections[p.product_id] || 0), 0);
  };

  const uniqueInGroup = (groupId) => {
    const g = groups.find(gr => gr.group_id === groupId);
    if (!g) return 0;
    return g.products.filter(p => (selections[p.product_id] || 0) > 0).length;
  };

  const canIncrement = (group, productId) =>
    groupTotal(group.group_id) < pack.multiples_of &&
    (group.max_unique == null ||
      (selections[productId] || 0) > 0 ||
      uniqueInGroup(group.group_id) < group.max_unique);

  const adjust = (productId, delta, group) => {
    setSelections(prev => {
      const cur  = prev[productId] || 0;
      const next = Math.max(0, cur + delta);
      if (next === 0) { const { [productId]: _, ...rest } = prev; return rest; }
      return { ...prev, [productId]: next };
    });
  };

  const handleAddPack = () => {
    const selectionSummary = groups.map(g => ({
      group_id: g.group_id, groupName: g.group_name,
      items: g.products
        .filter(p => (selections[p.product_id] || 0) > 0)
        .map(p => ({ product_id: p.product_id, name: p.product_name, qty: selections[p.product_id] })),
    }));
    addToCart({
      id: comboCartId(pack.menu_pack_id, selections),
      name: pack.pack_name, price: pack.price, isPack: true,
      pack_id: pack.pack_id, menu_pack_id: pack.menu_pack_id,
      multiples_of: pack.multiples_of, order_lead_days: pack.order_lead_days,
      order_cutoff_time: pack.order_cutoff_time, order_after_cutoff: pack.order_after_cutoff,
      days_available: pack.days_available, image_url: pack.image_url,
      selections, selectionSummary, addedAt: Date.now(),
    });
    onClose();
  };

  const handleRepeatLast = () => {
    addToCart({
      id: comboCartId(pack.menu_pack_id, prevSel),
      name: pack.pack_name, price: pack.price, isPack: true,
      pack_id: pack.pack_id, menu_pack_id: pack.menu_pack_id,
      multiples_of: pack.multiples_of, order_lead_days: pack.order_lead_days,
      order_cutoff_time: pack.order_cutoff_time, order_after_cutoff: pack.order_after_cutoff,
      days_available: pack.days_available, image_url: pack.image_url,
      selections: prevSel, selectionSummary: prevSummary, addedAt: Date.now(),
    });
    onClose();
  };

  const colClass = groups.length <= 2 ? 'pack-modal--2col'
                 : groups.length === 3 ? 'pack-modal--3col'
                 : 'pack-modal--4col';

  return (
    <div className="pack-overlay" onClick={onClose}>
      <div className={`pack-modal ${colClass}`} onClick={e => e.stopPropagation()}>
        <div className="pack-modal-hdr">
          <div>
            <h2>{pack.pack_name}</h2>
            <p>Pick {pack.multiples_of} from each group &nbsp;&middot;&nbsp; {formatPrice(pack.price)}</p>
          </div>
          <button className="pack-modal-close-btn" onClick={onClose}>&#10005;</button>
        </div>
        {loading && <div className="cat-state"><div className="cat-spinner" /><p>Loading&hellip;</p></div>}
        {error   && <div className="cat-state cat-state--error"><p>{error}</p></div>}
        {!loading && !error && (
          <>
            <div className="pack-modal-body">
              {groups.map(group => (
                <div key={group.group_id} className="pack-group-col">
                  <div className="pack-group-hdr">
                    <span className="pack-group-name">{group.group_name}</span>
                    {group.max_unique && <span className="pack-group-hint">Any {group.max_unique}</span>}
                  </div>
                  <div className="pack-group-items">
                    {group.products.map(prod => {
                      const qty   = selections[prod.product_id] || 0;
                      const isAuto = group.products.length === 1;
                      return (
                        <div key={prod.product_id} className={`pack-prod-card${isAuto ? ' pack-prod-card--auto' : ''}`}>
                          <div className="pack-prod-img-wrap">
                            {prod.image_url
                              ? <img src={resolveImg(prod.image_url)} alt={prod.product_name} className="pack-prod-img" />
                              : <div className="pack-prod-placeholder">&#128230;</div>
                            }
                          </div>
                          <div className="pack-prod-info">
                            <span className="pack-prod-name">{prod.product_name}</span>
                            {prod.price && <span className="pack-prod-price">{formatPrice(prod.price)}</span>}
                          </div>
                          <div className={`pack-prod-qty${isAuto ? ' pack-prod-qty--auto' : ''}`}>
                            <button className="qty-btn qty-btn--minus"
                              onClick={() => adjust(prod.product_id, -1, group)}
                              disabled={qty === 0 || isAuto}
                              aria-label="Remove one">&#8722;</button>
                            <span className="qty-value">{qty}</span>
                            <button className="qty-btn qty-btn--plus"
                              onClick={() => adjust(prod.product_id, 1, group)}
                              disabled={!canIncrement(group, prod.product_id) || isAuto}
                              aria-label="Add one">&#43;</button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
            <div className="pack-modal-footer">
              <div className="pack-tally">
                <span className={`pack-tally-count${totalSelected === totalRequired ? ' pack-tally-count--full' : ''}`}>
                  {totalSelected}&nbsp;/&nbsp;{totalRequired}
                </span>
                <span className="pack-tally-label">items selected</span>
              </div>
              <div className="pack-footer-actions">
                <button className="pack-btn pack-btn--cancel" onClick={onClose}>Cancel</button>
                {canRepeat && (
                  <button className="pack-btn pack-btn--repeat" onClick={handleRepeatLast}>Repeat Last</button>
                )}
                <button className="pack-btn pack-btn--add"
                  onClick={handleAddPack}
                  disabled={totalSelected !== totalRequired}>Add Pack</button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

// ── Pack grid ─────────────────────────────────────────────────────────────────
const PackGrid = ({ packs }) => {
  const { removeFromCart, cart } = useCart();
  const [activePack, setActivePack] = useState(null);

  if (!packs || packs.length === 0) return null;

  return (
    <>
      <div className="cat-product-grid cat-pack-section">
        {packs.map(pack => {
          const combos = Object.values(cart)
            .filter(item => item.product.isPack && item.product.menu_pack_id === pack.menu_pack_id)
            .sort((a, b) => (b.product.addedAt || 0) - (a.product.addedAt || 0));
          const qty    = combos.reduce((s, c) => s + c.quantity, 0);
          const lastId = combos[0]?.product?.id;
          const blocked = isPastCutoff(pack.order_cutoff_time, pack.order_after_cutoff);
          const deliveryLabel = qty > 0 ? computeDeliveryLabel({
            order_lead_days: pack.order_lead_days, order_cutoff_time: pack.order_cutoff_time,
            order_after_cutoff: pack.order_after_cutoff, days_available: pack.days_available,
          }) : null;
          return (
            <div key={pack.menu_pack_id} className={`cat-tile${blocked ? ' cat-tile--blocked' : ''}`}>
              <div className="cat-tile-image-wrap cat-pack-badge-wrap">
                {pack.image_url
                  ? <img src={resolveImg(pack.image_url)} alt={pack.pack_name} className="cat-tile-image" />
                  : <div className="cat-tile-image-placeholder">&#127873;</div>
                }
                <span className="cat-pack-badge">Pack</span>
              </div>
              <div className="cat-tile-info">
                <h3 className="cat-tile-name">{pack.pack_name}</h3>
                {pack.description && <p className="cat-tile-desc">{pack.description}</p>}
                <div className="cat-tile-price-row">
                  {formatPrice(pack.price) && <span className="cat-tile-price">{formatPrice(pack.price)}</span>}
                  {formatCutoffTime(pack.order_cutoff_time) && (
                    <span className="cat-tile-cutoff">Order By {formatCutoffTime(pack.order_cutoff_time)}</span>
                  )}
                </div>
              </div>
              <div className="cat-tile-qty">
                {qty > 0 && (
                  <>
                    <button className="qty-btn qty-btn--minus"
                      onClick={() => removeFromCart(lastId)}
                      aria-label="Remove one">&#8722;</button>
                    <span className="qty-value">{qty}</span>
                  </>
                )}
                <button
                  className={`pack-customise-btn${qty > 0 ? ' pack-customise-btn--more' : ''}`}
                  onClick={() => setActivePack(pack)}
                  disabled={blocked}>
                  {qty > 0 ? '+ Add More' : 'Customise & Add'}
                </button>
                {deliveryLabel && (
                  <span className="cat-tile-delivery-label">Delivery: {deliveryLabel}</span>
                )}
              </div>
            </div>
          );
        })}
      </div>
      {activePack && (
        <PackCustomiseModal pack={activePack} onClose={() => setActivePack(null)} />
      )}
    </>
  );
};

// ── Checkout floater ──────────────────────────────────────────────────────────
const CAMPAIGN_MIN = 499;

const CheckoutFloater = () => {
  const { cartCount, cartTotalWithoutFee, belowMinimum, hasExemptItem, minOrder, isCampaignFlow } = useCart();
  const navigate = useNavigate();
  if (cartCount === 0) return null;
  const belowCampaignMin = isCampaignFlow && cartTotalWithoutFee < CAMPAIGN_MIN;
  return (
    <div
      className={`checkout-floater${belowCampaignMin ? ' checkout-floater--disabled' : ''}`}
      onClick={belowCampaignMin ? undefined : () => navigate('/quick-register')}
    >
      <span className="checkout-floater-label">
        {belowCampaignMin && (
          <span className="checkout-floater-subfee">
            Add ₹{(CAMPAIGN_MIN - cartTotalWithoutFee).toFixed(0)} more to checkout
          </span>
        )}
        {!belowCampaignMin && belowMinimum && !hasExemptItem && !isCampaignFlow && (
          <span className="checkout-floater-subfee">
            Add ₹{(minOrder - cartTotalWithoutFee).toFixed(0)} for free delivery
          </span>
        )}
        <span>Proceed to Checkout</span>
      </span>
      <span className="checkout-floater-badge">{cartCount} item{cartCount !== 1 ? 's' : ''}</span>
      <span className="checkout-floater-arrow">→</span>
    </div>
  );
};

// ── Slim nav ──────────────────────────────────────────────────────────────────
const MenuNav = () => {
  const { cartCount } = useCart();
  const navigate = useNavigate();
  return (
    <nav className="sd-nav">
      <div className="sd-nav-logo" onClick={() => navigate('/')}>
        <img src="/logo.webp" alt="The New Luxury" className="sd-nav-logo-img" />
      </div>
      <div className="sd-nav-links">
        <button className="sd-nav-link sd-nav-cart" onClick={() => navigate('/cart')}>
          <span className="sd-nav-cart-icon">&#128722;</span>
          {cartCount > 0 && <span className="sd-nav-cart-badge">{cartCount}</span>}
        </button>
      </div>
    </nav>
  );
};

// ── Main MenuLanding page ─────────────────────────────────────────────────────
const MenuLanding = () => {
  const { menu_code } = useParams();
  const { user, loading: authLoading } = useContext(AuthContext);

  const [menu, setMenu]               = useState(null);
  const [menuLoading, setMenuLoading] = useState(true);
  const [menuError, setMenuError]     = useState(null);

  const [path, setPath]                       = useState([]);
  const childrenMapRef                        = useRef({});
  const [childrenMap, setChildrenMap]         = useState({});
  const [childrenLoading, setChildrenLoading] = useState({});

  const [products, setProducts]               = useState([]);
  const [productsLoading, setProductsLoading] = useState(false);
  const [productsError, setProductsError]     = useState(null);
  const [packs, setPacks]                     = useState([]);
  const [menuPages, setMenuPages]             = useState([]);

  // Flag this session as a campaign flow so the delivery fee is suppressed
  useEffect(() => {
    sessionStorage.setItem('lh_campaign', '1');
  }, []);

  // Load menu by code on mount
  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${PUB}/by-code`, { params: { menu_code } });
        setMenu(res.data);
      } catch (err) {
        setMenuError(err.response?.status === 404 ? 'Menu not found.' : 'Failed to load menu.');
      } finally {
        setMenuLoading(false);
      }
    })();
  }, [menu_code]);

  const fetchProducts = useCallback(async (scopeMenuId) => {
    setProductsLoading(true);
    setProductsError(null);
    setMenuPages([]);
    setPacks([]);
    try {
      const [prodRes, pagesRes] = await Promise.all([
        axios.get(`${PUB}/products`,   { params: { menu_id: scopeMenuId } }),
        axios.get(`${PUB}/menu-pages`, { params: { menu_id: scopeMenuId } }),
      ]);
      setProducts(prodRes.data);
      setMenuPages(pagesRes.data);
    } catch {
      setProductsError('Failed to load products. Please try again.');
    } finally {
      setProductsLoading(false);
    }
    axios.get(`${PUB}/menu-packs`, { params: { menu_id: scopeMenuId } })
      .then(r => setPacks(r.data))
      .catch(() => {});
  }, []);

  const fetchChildren = useCallback(async (parentId) => {
    if (childrenMapRef.current[parentId] !== undefined) return childrenMapRef.current[parentId];
    setChildrenLoading(p => ({ ...p, [parentId]: true }));
    try {
      const res = await axios.get(`${PUB}/children`, { params: { parent_id: parentId } });
      childrenMapRef.current[parentId] = res.data;
      setChildrenMap(p => ({ ...p, [parentId]: res.data }));
      return res.data;
    } catch {
      childrenMapRef.current[parentId] = [];
      setChildrenMap(p => ({ ...p, [parentId]: [] }));
      return [];
    } finally {
      setChildrenLoading(p => ({ ...p, [parentId]: false }));
    }
  }, []);

  // Once menu is resolved, seed initial path from its children
  useEffect(() => {
    if (!menu) return;
    (async () => {
      await fetchChildren(menu.id);
      setPath([{ parentId: menu.id, selectedId: 'all' }]);
    })();
  }, [menu?.id]); // eslint-disable-line

  // Path change → fetch products for the active scope
  useEffect(() => {
    if (path.length === 0) return;
    const last = path[path.length - 1];
    const scopeId = last.selectedId === 'all' ? last.parentId : last.selectedId;
    fetchProducts(scopeId);
  }, [path, fetchProducts]);

  const handleTabSelect = useCallback(async (levelIndex, menuId) => {
    if (menuId === 'all') {
      setPath(prev => [...prev.slice(0, levelIndex), { ...prev[levelIndex], selectedId: 'all' }]);
      return;
    }
    const children = await fetchChildren(menuId);
    setPath(prev => {
      const updated = [...prev.slice(0, levelIndex), { ...prev[levelIndex], selectedId: menuId }];
      if (children.length > 0) updated.push({ parentId: menuId, selectedId: 'all' });
      return updated;
    });
  }, [fetchChildren]);

  // If logged in, go to categories
  if (!authLoading && user) return <Navigate to="/categories" replace />;

  if (authLoading || menuLoading) {
    return (
      <div className="sd-page">
        <MenuNav />
        <div className="cat-state"><div className="cat-spinner" /><p>Loading&hellip;</p></div>
      </div>
    );
  }

  if (menuError) {
    return (
      <div className="sd-page">
        <MenuNav />
        <div className="cat-state cat-state--error"><p>{menuError}</p></div>
      </div>
    );
  }

  return (
    <div className="sd-page">
      <MenuNav />
      <CheckoutFloater />
      <div className="categories-container sd-content">
        {path.map((level, i) => {
          const children = childrenMap[level.parentId] || [];
          const isLoading = !!childrenLoading[level.parentId];
          if (!isLoading && children.length === 0) return null;
          return (
            <TabRow
              key={level.parentId}
              menus={children}
              selectedId={level.selectedId}
              onSelect={menuId => handleTabSelect(i, menuId)}
              loading={isLoading}
            />
          );
        })}
        <PackGrid packs={packs} />
        <ProductGrid
          products={products}
          loading={productsLoading}
          error={productsError}
          menuPages={menuPages}
        />
      </div>
    </div>
  );
};

export default MenuLanding;
